import React, { useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { CATEGORIES } from "../mock";
import { useProducts } from "../hooks/useCatalog";
import { useCart } from "../context/CartContext";
import { Plus, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";

const tileVariants = ["tile-rose", "tile-cream"];

const Shop = () => {
  const { categoryId } = useParams();
  const { addItem } = useCart();
  const { products: allProducts } = useProducts();
  const [sort, setSort] = useState("featured");

  const activeCat = categoryId ? CATEGORIES.find((c) => c.id === categoryId) : null;

  const products = useMemo(() => {
    let list = categoryId ? allProducts.filter((p) => p.category === categoryId) : allProducts;
    if (sort === "price-asc") list = [...list].sort((a, b) => a.price - b.price);
    if (sort === "price-desc") list = [...list].sort((a, b) => b.price - a.price);
    return list;
  }, [categoryId, sort, allProducts]);

  const handleAdd = (e, p) => {
    e.preventDefault();
    e.stopPropagation();
    if (p.stock_status === "out_of_stock") return toast.error(`${p.name} is out of stock.`);
    addItem(p);
    toast.success(`${p.name} — added to your cart.`);
  };

  return (
    <main className="min-h-screen bg-charcoal pt-6">
      <section className="px-6 md:px-10 max-w-[1600px] mx-auto pt-16 pb-10 border-b border-off-white/10">
        <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] text-off-white/50 mb-6">
          <Link to="/" className="hover:text-rose">HOME</Link>
          <span>/</span>
          <Link to="/shop" className="hover:text-rose">SHOP</Link>
          {activeCat && (<><span>/</span><span className="text-rose">{activeCat.name.toUpperCase()}</span></>)}
        </div>

        <div className="grid md:grid-cols-12 gap-6 items-end">
          <div className="md:col-span-8">
            <p className="font-mono text-[10px] tracking-[0.3em] text-rose mb-3">
              // {activeCat ? activeCat.tag : "ALL / INVENTORY"}
            </p>
            <h1 className="font-serif-display italic text-5xl md:text-7xl leading-[0.95] text-off-white">
              {activeCat ? activeCat.name : "Everything, whipped."}
            </h1>
            <p className="text-off-white/65 mt-4 max-w-xl font-light">
              {activeCat ? activeCat.blurb : "The complete catalog. Micro-batch, cold-whipped, engineered for absorption."}
            </p>
          </div>
          <div className="md:col-span-4 flex md:justify-end items-center gap-4">
            <SlidersHorizontal size={16} className="text-off-white/60" />
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="bg-transparent border border-off-white/20 h-11 px-3 font-mono text-[11px] tracking-[0.2em] text-off-white focus:border-rose outline-none transition-colors-only"
            >
              <option value="featured">FEATURED</option>
              <option value="price-asc">PRICE ↑</option>
              <option value="price-desc">PRICE ↓</option>
            </select>
          </div>
        </div>
      </section>

      {/* Category chips */}
      <section className="px-6 md:px-10 max-w-[1600px] mx-auto py-6 border-b border-off-white/10 flex flex-wrap gap-3">
        <Link
          to="/shop"
          className={`px-4 h-10 inline-flex items-center border font-mono text-[10px] tracking-[0.25em] uppercase transition-colors-only ${
            !categoryId ? "border-off-white bg-off-white text-charcoal" : "border-off-white/20 text-off-white/75 hover:border-off-white"
          }`}
        >
          ALL
        </Link>
        {CATEGORIES.map((c) => (
          <Link
            key={c.id}
            to={`/shop/${c.id}`}
            className={`px-4 h-10 inline-flex items-center border font-mono text-[10px] tracking-[0.25em] uppercase transition-colors-only ${
              categoryId === c.id ? "border-off-white bg-off-white text-charcoal" : "border-off-white/20 text-off-white/75 hover:border-off-white"
            }`}
          >
            {c.name}
          </Link>
        ))}
      </section>

      {/* Grid */}
      <section className="px-6 md:px-10 max-w-[1600px] mx-auto py-14">
        {products.length === 0 ? (
          <p className="font-mono text-sm text-off-white/50 py-20 text-center">// NO ITEMS IN THIS COLLECTION</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {products.map((p, i) => (
              <Link
                to={`/product/${p.id}`}
                key={p.id}
                className="group relative border border-off-white/10 hover:border-off-white transition-colors-only flex flex-col bg-charcoal"
              >
                <div className={`aspect-[4/5] overflow-hidden relative flex items-center justify-center p-6 ${tileVariants[i % 2]}`}>
                  <img src={p.image} alt={p.name} className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute top-3 left-3 flex items-center gap-2">
                    <span className="font-mono text-[10px] tracking-[0.25em] text-off-white/70">0{i + 1}</span>
                    {p.badge && (
                      <span className="font-mono text-[9px] tracking-[0.25em] px-2 py-0.5 bg-off-white text-charcoal font-semibold">
                        {p.badge}
                      </span>
                    )}
                    {p.stock_status === "out_of_stock" && (
                      <span className="font-mono text-[9px] tracking-[0.25em] px-2 py-0.5 bg-charcoal text-off-white border border-off-white/40">
                        OUT OF STOCK
                      </span>
                    )}
                  </div>
                  <button
                    onClick={(e) => handleAdd(e, p)}
                    disabled={p.stock_status === "out_of_stock"}
                    className="absolute bottom-3 right-3 w-10 h-10 border border-off-white bg-charcoal/90 backdrop-blur-sm hover:bg-off-white hover:text-charcoal flex items-center justify-center transition-colors-only disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Add to cart"
                  >
                    <Plus size={16} strokeWidth={1.5} />
                  </button>
                </div>
                <div className="p-4 flex-1 flex flex-col bg-charcoal">
                  <h3 className="font-serif-display italic text-xl text-off-white leading-tight">{p.name}</h3>
                  <p className="text-xs text-off-white/55 mt-1 line-clamp-1">{p.tagline}</p>
                  <div className="mt-auto pt-3 flex items-center justify-between">
                    <span className="font-mono text-[12px] text-off-white">${p.price.toFixed(2)} USD</span>
                    {p.originalPrice && (
                      <span className="font-mono text-[11px] text-off-white/40 line-through">${p.originalPrice.toFixed(2)}</span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </main>
  );
};

export default Shop;
